import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { DataType } from 'constants/taskCategories';
import searchItems from 'utils/searchItems';
import { replaceValuesInsideCurlyBrackets } from 'utils/string';

import { getDebouncedSearchValue } from 'store/search/selectors';
import { selectTaskTypeDynamicField } from 'store/system/selectors';

import { selectDealFromApplication } from './deal.selectors';
import { selectLoanApplication } from './loanApplication.selectors';

export const selectTasks = createSelector(selectLoanApplication, ({ tasks }) => tasks);

const selectTaskSlaGroups = createSelector(
  selectLoanApplication,
  ({ taskSlaGroup }) => taskSlaGroup,
);

const selectTasksList = createSelector(selectTasks, Object.values);

const selectTasksWithFormattedName = createSelector(
  [selectTasksList, selectTaskTypeDynamicField, selectDealFromApplication],
  (tasks, dynamicFieldOptions, deal) =>
    tasks.map(({ name, ...task }) => {
      return {
        ...task,
        loanId: deal?.loanId,
        name: replaceValuesInsideCurlyBrackets({
          string: name,
          options: dynamicFieldOptions,
          keyToReplace: 'value',
          keyToReplaceWith: 'name',
        }),
      };
    }),
);

const selectTasksWithSlaGroupName = createSelector(
  [selectTasksWithFormattedName, selectTaskSlaGroups],
  (tasks, taskSlaGroups) =>
    tasks.map(task => ({
      ...task,
      slaGroupName: taskSlaGroups[task.slaGroupId]?.name,
    })),
);

const selectFilteredTasksList = createSelector(
  getDebouncedSearchValue,
  selectTasksWithSlaGroupName,
  searchItems(({ name }) => [name]),
);

export const selectGroupedFilteredTasks = createSelector(selectFilteredTasksList, tasksList =>
  _.groupBy(tasksList, 'status'),
);

export const selectTasksByIds = memoizeOne(taskIds =>
  createSelector(selectTasksWithFormattedName, tasks =>
    tasks.filter(({ taskId }) => taskIds?.includes(taskId)),
  ),
);

export const selectBrokerTasksByConditionId = memoizeOne(conditionId =>
  createSelector(selectTasksWithFormattedName, tasks =>
    tasks.filter(task => task?.metadata?.includes(conditionId)),
  ),
);

export const selectScheduleClosingTask = createSelector(selectTasksWithFormattedName, tasks =>
  tasks.find(({ dataType }) => dataType === DataType.SCHEDULE_CLOSING),
);
